import { ActionIcon, Box, Button, Flex, Title, Tooltip } from '@mantine/core';
import {
  QuestionnaireItem,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import { QuestionnaireRepeatableItem } from '../QuestionnaireFormItem/QuestionnaireRepeatableItem';
import {
  buildInitialResponseItem,
  QuestionnaireItemType,
} from '@medplum/react';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { RENDERING_STYLE_EXTENSION_URL } from '@consts/systems.consts';

interface QuestionnaireRepeatableGroupProps {
  readonly item: QuestionnaireItem;
  readonly response: QuestionnaireResponseItem[];
  readonly checkForQuestionEnabled: (
    item: QuestionnaireItem,
    contextResponse?: any
  ) => boolean;
  readonly onChange: (responses: QuestionnaireResponseItem[]) => void;
}

export const QuestionnaireRepeatedGroup = (
  props: QuestionnaireRepeatableGroupProps
): JSX.Element | null => {
  // Use props.response directly as the single source of truth
  const responses = props.response || [];

  if (responses.length === 0) {
    return null;
  }

  function handleRepeatableGroup(
    newResponseItems: QuestionnaireResponseItem[],
    index: number
  ): void {
    const newResponses = responses.map((response, idx) =>
      idx === index ? newResponseItems[0] : response
    );

    props.onChange(newResponses);
  }

  function insertNewGroup(): void {
    const cleanItem = {
      ...props.item,
      initial: undefined, // Remove and inherited initial values
      item: props.item.item?.map((childItem) => ({
        ...childItem,
        initial: undefined, // Remove inital values from child items
      })),
    };
    const newResponse = buildInitialResponseItem(cleanItem);

    const newResponses = [...responses, newResponse];
    props.onChange(newResponses);
  }

  //----------------------------------------------------------------------------

  function removeGroup(index: number): void {
    const newResponses = responses.filter((_, idx) => idx !== index);

    props.onChange(newResponses);
  }

  //----------------------------------------------------------------------------
  return (
    <>
      {responses.map((response, idx) => (
        <Box
          key={`${response.linkId}-${response.id || idx}-${idx}`}
          className="flex items-center gap-2"
        >
          <QuestionnaireGroup
            key={`${response.linkId}-${response.id || idx}-${idx}`}
            item={props.item}
            response={response}
            checkForQuestionEnabled={(item, _contextResponse) =>
              props.checkForQuestionEnabled(item, response)
            }
            onChange={(r) => handleRepeatableGroup(r, idx)}
          />
          {idx > 0 && props.checkForQuestionEnabled(props.item) && (
            <Tooltip label="Αφαίρεση">
              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => removeGroup(idx)}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Box>
      ))}
      {props.item.repeats && props.checkForQuestionEnabled(props.item) && (
        <Button
          leftSection={<IconPlus size={16} />}
          variant="light"
          size="sm"
          onClick={insertNewGroup}
        >
          Προσθήκη
        </Button>
      )}
    </>
  );
};

interface QuestionnaireGroupProps {
  readonly item: QuestionnaireItem;
  readonly response: QuestionnaireResponseItem;
  readonly checkForQuestionEnabled: (
    item: QuestionnaireItem,
    contextResponse?: any
  ) => boolean;
  readonly onChange: (response: QuestionnaireResponseItem[]) => void;
}

export const QuestionnaireGroup = (
  props: QuestionnaireGroupProps
): JSX.Element | null => {
  const { response, checkForQuestionEnabled, onChange } = props;

  //----------------------------------------------------------------------------
  /**
   * Extracts styling information for a group.
   *
   * - Default: `flex-direction: column`
   * - Our use case: `flex-direction: row`
   */
  const renderingStyle =
    props.item.extension?.find(
      (ext) => ext.url === RENDERING_STYLE_EXTENSION_URL
    )?.valueString ?? 'flex-col flex-wrap gap-4 w-full';

  //----------------------------------------------------------------------------
  function onSetGroup(newResponseItem: QuestionnaireResponseItem[]): void {
    const currentItems = response.item || [];
    const linkId =
      newResponseItem.length > 0 ? newResponseItem[0].linkId : null;

    if (!linkId) {
      return;
    }

    // Check if this is a removal operation by comparing with current items
    const existingItemsWithLinkId = currentItems.filter(
      (item) => item.linkId === linkId
    );

    // If newResponseItem has fewer items than existing, it's a removal
    if (newResponseItem.length < existingItemsWithLinkId.length) {
      const otherItems = currentItems.filter((item) => item.linkId !== linkId);
      const updatedItems = [...otherItems, ...newResponseItem];

      const groupResponse = { ...response, item: updatedItems };
      onChange([groupResponse]);
      return;
    }

    // For updates/additions, use the original logic
    const otherItems = currentItems.filter((item) => item.linkId !== linkId);
    const updatedItems = [...otherItems, ...newResponseItem];

    const groupResponse = { ...response, item: updatedItems };
    onChange([groupResponse]);
  }

  if (!props.checkForQuestionEnabled(props.item)) {
    return null;
  }

  return (
    <div
      className="flex flex-col w-full justify-between"
      key={props.item.linkId}
    >
      {props.item.text && (
        <Title order={3} mb="md">
          {props.item.text}
        </Title>
      )}

      <Flex className={renderingStyle}>
        {props.item.item?.map((childItem) =>
          renderItem(
            childItem,
            response,
            checkForQuestionEnabled,
            onSetGroup,
            response
          )
        )}
      </Flex>
    </div>
  );
};

function renderItem(
  item: QuestionnaireItem,
  response: QuestionnaireResponseItem,
  checkForQuestionEnabled: (
    item: QuestionnaireItem,
    contextResponse?: any
  ) => boolean,
  onSetGroup: (response: QuestionnaireResponseItem[]) => void,
  _contextResponse?: QuestionnaireResponseItem
) {
  if (item.type === QuestionnaireItemType.group) {
    const filteredResponse =
      response.item?.filter((i) => i.linkId === item.linkId) ?? [];

    return item.repeats ? (
      <QuestionnaireRepeatedGroup
        key={`${item.linkId}-${response.id || 'root'}`}
        item={item}
        response={filteredResponse}
        checkForQuestionEnabled={checkForQuestionEnabled}
        onChange={onSetGroup}
      />
    ) : (
      <QuestionnaireGroup
        key={item.linkId}
        item={item}
        checkForQuestionEnabled={checkForQuestionEnabled}
        response={
          response.item?.find((i) => i.linkId === item.linkId) ?? {
            linkId: item.linkId,
          }
        }
        onChange={onSetGroup}
      />
    );
  }

  // For repeatable items, we need to find the response item that contains all answers
  // If there are multiple response items with the same linkId, we need to merge them
  const responseItems =
    response.item?.filter((i) => i.linkId === item.linkId) ?? [];
  let mergedResponse: QuestionnaireResponseItem | undefined;

  if (responseItems.length > 0) {
    // If there's only one response item, use it directly
    if (responseItems.length === 1) {
      mergedResponse = responseItems[0];
    } else {
      // If there are multiple response items with the same linkId, merge their answers
      const allAnswers = responseItems.flatMap((r) => r.answer ?? []);
      mergedResponse = {
        ...responseItems[0],
        answer: allAnswers,
      };
    }
  }

  return (
    <QuestionnaireRepeatableItem
      key={item.linkId}
      item={item}
      response={mergedResponse}
      onChange={onSetGroup}
      checkForQuestionEnabled={checkForQuestionEnabled}
    />
  );
}
